'use client';

import { useState } from 'react';
import { registerWithCheckout } from '@/shared/api/auth/register-with-checkout';
import type { RegistrationFormData } from '@/shared/lib/validation/registration';
import type { RegisterWithCheckoutPayload } from './register-with-checkout-payload';


export type RegisterError =
    | { kind: 'email_exists' }
    | { kind: 'generic'; message: string };


/**
 * Builds the flat RegistrationFormData shape expected by the BFF endpoint
 * from the wizard's split-form data.
 */
const toApiFormData = (payload: RegisterWithCheckoutPayload): RegistrationFormData => ({
    address                 : payload.company.address,
    billing_address         : payload.billing_address.billing_address,
    billing_address_extra   : payload.billing_address.billing_address_extra,
    billing_city            : payload.billing_address.billing_city,
    billing_country         : payload.billing_address.billing_country,
    billing_email           : payload.billing_address.billing_email,
    billing_email_additional: payload.billing_address.billing_email_additional ?? '',
    billing_period          : payload.plan.billing_period,
    billing_postal_code     : payload.billing_address.billing_postal_code,
    city                    : payload.company.city,
    company_name            : payload.company.company_name,
    company_vat             : payload.company.company_vat,
    country                 : payload.company.country,
    email                   : payload.credentials.email,
    first_name              : payload.profile.first_name,
    job_title               : payload.profile.job_title,
    last_name               : payload.profile.last_name,
    password                : payload.credentials.password,
    phone                   : payload.profile.phone,
    plan_id                 : payload.plan.plan_id,
    postal_code             : payload.company.postal_code,
    terms                   : payload.credentials.terms,
    vat_id                  : payload.billing_address.vat_id,
});


export const useRegisterWithCheckout = () => {
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ]     = useState<RegisterError | null>(null);

    const submit = async (payload: RegisterWithCheckoutPayload) => {
        setLoading(true);
        setError(null);

        const result = await registerWithCheckout(toApiFormData(payload));

        setLoading(false);

        if (!result.ok) {
            if (result.error === 'email_already_registered') {
                setError({ kind: 'email_exists' });
            } else {
                setError({ kind: 'generic', message: result.error });
            }
            return null;
        }

        return { checkoutUrl: result.checkoutUrl };
    };

    return { submit, loading, error, setError };
};
